import {centroid, directionTo, dot} from "../graph/vec3";
import {type Feature, Features} from "../feature";

export const featureSorter = {
    sort: (features: Feature[]) => {
        // DFS sorting
        const unvisited = features.slice();
        const candidates: Feature[] = [];
        const sorted: Feature[] = [];
        while (unvisited.length > 0) {
            const first = featureSorter.getLargestFeature(unvisited)!;
            candidates.push(first);
            while (candidates.length > 0) {
                const parent = candidates.pop()!;
                // PIPH-149: A feature may be added to candidates multiple times if it's
                // attached to multiple parents (e.g., via alkene connection). Skip if
                // already processed to prevent duplicates in the sorted list.
                const idx = unvisited.indexOf(parent);
                if (idx === -1) continue;
                unvisited.splice(idx, 1);
                sorted.push(parent);

                // Attached features are features that are associated with the parent feature
                // Children features are features that live entirely in the parent feature (alkenes, etc...)
                let attached = featureSorter.getAttachedFeatures(parent, unvisited);
                const children = featureSorter.getChildrenFeatures(parent, attached);
                attached = attached.filter(f => !children.includes(f));

                // Each attached feature has a sibling connection to the parent feature.
                // PIPH-100: Both directions are needed for bidirectional connection info.
                attached.forEach(f => {
                    const attachmentPoints = f.connections.filter(a => parent.atoms.includes(a));

                    // Attached → Parent relationship
                    const attachedToParentRelationship = {feature: parent, connections: attachmentPoints};
                    f.siblings = f.siblings ? [...f.siblings, attachedToParentRelationship] : [attachedToParentRelationship];

                    // Parent → Attached relationship (PIPH-100: enables bidirectional connections)
                    const parentToAttachedRelationship = {feature: f, connections: attachmentPoints};
                    parent.siblings = parent.siblings ? [...parent.siblings, parentToAttachedRelationship] : [parentToAttachedRelationship];
                })

                // Each child feature has a parent.
                children.forEach(f => {
                    f.parent = parent;

                    // Consider each child visited.
                    unvisited.splice(unvisited.indexOf(f), 1);
                })

                // Parents have children.
                parent.children = [...children];
                sorted.push(...children);


                // Now look through all the attached features.
                // NOTE: Children are done at this point.
                candidates.push(...attached);
            }
        }
        return sorted;
    },
    getLargestFeature: (features: Feature[]) => {
        // NOTE: pop takes off the bottom, so we sort low to high
        // In case of tie, there's nothing special at the moment. This may be a place
        // to do constituent sorting as well.
        return features.slice().sort((a, b) => featureSorter.getFeatureScore(a) - featureSorter.getFeatureScore(b)).pop();
    },
    getFeatureScore: (f: Feature) => {
        const ringChainMultiplier = f.name.includes(Features.ring) || f.name.includes(Features.chain) ? 1000 : 1
        const score = (Math.pow(f.atoms.length, 2) + f.connections.length) * ringChainMultiplier;
        return score;
    },
    getAttachedFeatures: (host: Feature, features: Feature[]) => {
        // Attached features have a connection on the host feature
        return features.filter(f => f.connections.some(a => host.atoms.includes(a)))
    },
    getChildrenFeatures: (parent: Feature, features: Feature[]) => {
        // Chidren features have every atom included in the parent.
        return features.filter(f => f !== parent && f.atoms.every(a => parent.atoms.includes(a)))
    },
    // Positionally sorted atoms are useful for paper-based solutions where
    // relativity from the top corner of the paper can be useful.
    positionallySortChainAtoms: (feature: Feature, invertY?: boolean) => {
        if (feature.atoms.length > 1) {
            // Sort the atom positions left-to-right or top-to-bottom
            const first = feature.atoms[0];
            const last = feature.atoms[feature.atoms.length - 1];
            const dx = Math.abs(first.pos![0] - last.pos![0]);
            const dy = Math.abs(first.pos![1] - last.pos![1]);
            if (dx >= dy) {
                // Sorting left-to-right
                if (first.pos![0] < last.pos![0]) return feature.atoms;
                else return feature.atoms.reverse();
            } else {
                // Sorting top-to-bottom

                // Computer paper-based coordinate have Y growing down the page
                if (invertY) {
                    if (first.pos![1] < last.pos![1]) return feature.atoms;
                    else return feature.atoms.reverse();
                } else {
                    if (first.pos![1] < last.pos![1]) return feature.atoms;
                    else return feature.atoms.reverse();
                }
            }
        } else {
            return feature.atoms
        }
    },
    positionallySortRingAtoms: (feature: Feature, invertY?: boolean) => {
        if(feature.atoms.length < 2) {
            return feature.atoms;
        }

        const positions = feature.atoms.map(a => ({pos: a.pos!, atom: a}));
        const c = centroid(positions.map(p => p.pos));
        const fromCentroids = positions.map(p => ({...p, dir: directionTo(c, p.pos)}));

        const compareByDirection = (a: [number,number,number], b: [number,number,number], target: [number,number,number], bias?: [number,number,number]) => {
            const scoreA = (2 * dot(target, a)) + (bias ? dot(bias, a) : 0);
            const scoreB = (2 * dot(target, b)) + (bias ? dot(bias, b) : 0);
            return scoreB - scoreA;
        }

        // First sort by high noon with a bias clockwise (3 o'clock).
        const highNoon: [number,number,number] = invertY ? [0,-1,0] : [0,1,0]
        fromCentroids.sort((a, b) =>
            compareByDirection(a.dir, b.dir, highNoon, [1,0,0])
        )

        // The first atom is now the root of the ring.
        const ringAtoms = [fromCentroids.shift()!];
        while(fromCentroids.length > 1) {
            const targetDir = ringAtoms[ringAtoms.length - 1].dir;
            fromCentroids.sort((a, b) => compareByDirection(a.dir, b.dir, targetDir, [1,0,0]));
            ringAtoms.push(fromCentroids.shift()!);
        }

        // Last one in the chain gets put on automatically.
        ringAtoms.push(fromCentroids.shift()!);

        return ringAtoms.map(a => a.atom);
    },
    sortTopLevelFeatures: (allFeatures: Feature[]) => {
        // Remove child features from the general list.
        const topLevelFeatures = allFeatures.slice().filter(f => !f.parent);

        topLevelFeatures.sort((a, b) => {
            // Put chains and rings at the top of the list.
            const isHeavyFeatureA = a.name.includes(Features.chain) || a.name.includes(Features.ring);
            const isHeavyFeatureB = b.name.includes(Features.chain) || b.name.includes(Features.ring);
            if(isHeavyFeatureA && !isHeavyFeatureB) {
                return -1; // A
            } else if(!isHeavyFeatureA && isHeavyFeatureB) {
                return 1; // B
            } else {
                return b.atoms.length - a.atoms.length;
            }
        });

        return topLevelFeatures;
    }
}
